import Form, {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import { getListApi } from '../../apiUtils';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { ApplicationUser } from '../../types/applicationUser.types';
import { Asset } from '../../types/asset.types';
import { AssetClass } from '../../types/assetClass.types';
import { IForm } from '../../types/iForm';
import {
  TerminalMappingCreateInput,
  TerminalMappingUpdateInput,
} from '../../types/terminalMapping.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { formTranslationByEdit } from '../../utils/formTranslationByEdit';

const dxSelectBoxConfig = {
  searchEnabled: true,
  searchModeOption: 'contains',
  searchExprOption: 'name',
  searchTimeoutOption: 200,
  showClearButton: true,
};

interface Props extends IForm {
  formData: TerminalMappingCreateInput | TerminalMappingUpdateInput;
}

export const TerminalMappingForm = ({ onSubmit, formData, isEdit }: Props) => {
  const { translate } = useTranslation();
  const { startLoading, stopLoading, loading } = useLoading();
  const history = useHistory();
  const transFormName = formTranslationByEdit('TerminalMapping', isEdit);
  const backRoute = '/admin-terminal-mapping';
  const formRef = useRef<Form>(null);

  const [prompt, setDirty, setClean, handleCloseButton] = useUnsavedChangesWarning(
    backRoute,
    () => {
      if (!formRef) return;
      if (formRef.current!.instance.validate().isValid) {
        setClean();
        onSubmit(null).then((result) => {
          if (result) history.push(backRoute);
          else setDirty();
        });
      }
    },
  );

  const { data: users } = useSWR<ApplicationUser[]>(API_URL.PERSON_LIST__NEW, fetcher);
  const { data: assetClasses } = useSWR<AssetClass[]>(API_URL.ASSET_CLASS_LIST, fetcher);
  const [assets, setAssets] = useState<Asset[]>([]);

  useEffect(() => {
    fetchAssets(formData.defaultAssetClass || undefined);
  }, []);

  const fetchAssets = async (assetClassId?: number) => {
    await getListApi({
      url: assetClassId
        ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClassId}`
        : `${API_URL.ASSET_LIST}`,
      params: {},
      callAfterSuccess: async (assets: Asset[]) => {
        setAssets(assets);
      },
      hideNotifications: true,
    });
  };

  const handleAssetClassChanged = (e: any) => {
    fetchAssets(e.value);
  };

  return (
    <form
      onSubmit={(e) => {
        onSubmit(e);
        if (formRef && formRef.current && !isEdit) formRef.current?.instance.resetValues();
        // @ts-ignore
        setClean();
      }}
    >
      <Form
        colCount={1}
        formData={formData}
        showValidationSummary={true}
        onFieldDataChanged={(e) => {
          // @ts-ignore
          setDirty();
        }}
        ref={formRef}
      >
        <GroupItem>
          <SimpleItem dataField="ip" editorType="dxTextBox">
            <Label text={translate!('Ip address', transFormName)} />
            <RequiredRule message={translate!('Ip address is required', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem
            dataField="defaultUserId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              items: users,
              valueExpr: 'id',
              displayExpr: 'name',
            }}
          >
            <Label text={translate!('Default user', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem
            dataField="defaultAssetClass"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              items: assetClasses,
              valueExpr: 'id',
              displayExpr: 'nameTranslated',
              onValueChanged: (val) => handleAssetClassChanged(val),
            }}
          >
            <Label text={translate!('Asset class', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem
            dataField="assignedAssetId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              items: assets,
              valueExpr: 'id',
              displayExpr: 'name',
            }}
          >
            <Label text={translate!('Default asset', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem dataField="description" editorType="dxTextArea">
            <Label text={translate!('Description', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem colCount={14}>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Close', ``)}
              type={'normal'}
              useSubmitBehavior={false}
              onClick={handleCloseButton}
            />
          </ButtonItem>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Save', ``)}
              type={'success'}
              disabled={loading}
              useSubmitBehavior
            />
          </ButtonItem>
        </GroupItem>
        {prompt}
      </Form>
    </form>
  );
};
